<template>
  <div>
    <div
      data-testid="edit-form"
      class="configuration-form"
      v-if="!showNotFoundPage"
    >
      <PageTitle :back-to-route="backToRoute" />
      <VerticalWizard
        :current-step="currentStep"
        :submitButtonText="submitButtonText"
        :submitButtonEnabled="canSubmit"
        @submit="submit"
        v-if="!isLoading"
        :submitting="isSubmitted"
      >
        <ConfigurationForm
          v-model="configuration"
          :validationsSchema="configurationModel"
          :submitButtonText="nextButtonText"
          :showSubmitButton="currentStep === 0"
          :rosterConfigurationList="rosterConfigurationList"
          @next="next"
          @input="onInput"
          :isNewRecord="isNewRecord"
          v-if="isRosterConfigurationListLoaded"
        />
        <PreferencesForm
          v-model="preferences"
          :validationsSchema="preferencesModel"
          :submitButtonText="nextButtonText"
          :showSubmitButton="currentStep === 1"
          @next="next"
          @input="onInput"
        />
        <DetailsForm
          v-model="details"
          :validationsSchema="detailsModel"
          @input="onInput"
        />
      </VerticalWizard>
      <NavigateAwayModal
        :showModal="leavePage != null"
        @continue-modal="onLeaveFunction"
        @cancel-modal="leavePage = null"
      />
      <SavedModal
        :showModal="showSavedModal"
        @continue-modal="navigateToDetails"
      />

      <AlModal
        :config="roleMatchModalConfig"
        :open-modal="isRoleMatchModel"
        @close="closeIsRoleMatchWarningModal"
      >
        <span>
          <Localization message="role-match-warning-message" />
        </span>
      </AlModal>

      <Toast v-model="showCreatedToast" type="info" :duration="5000">
        <template v-slot:title
          ><Localization message="create-plan-created-toast-title"
        /></template>
        <Localization message="create-plan-created-toast-message" />
      </Toast>
      <Toast v-model="showFailedToast" type="error" :duration="5000">
        <template v-slot:title
          ><Localization message="create-plan-failed-toast-title"
        /></template>
        <Localization message="create-plan-failed-toast-message" />
      </Toast>
    </div>
    <div>
      <NotFound v-if="showNotFoundPage" />
    </div>
  </div>
</template>

<script>
import { required, between, maxLength } from "vuelidate/lib/validators";
import ConfigurationForm from "./formParts/Configuration.vue";
import PreferencesForm from "./formParts/Preferences.vue";
import DetailsForm from "./formParts/Details.vue";
import {
  PageTitle,
  VerticalWizard,
  DateValidators,
  CapacityPlannerConfiguration,
  Translator,
  Toast,
  Localization,
  NotFound,
  Me,
  User,
  RosterConfiguration,
} from "@agyletime/wfm-vue-common";
import NavigateAwayModal from "./formParts/NavigateAwayModal.vue";
import SavedModal from "./formParts/SavedModal.vue";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { equals } from "ramda";
import { AlModal } from "@playvox/almendra-components";

export default {
  name: "ConfigurationPage",
  components: {
    PageTitle,
    VerticalWizard,
    ConfigurationForm,
    PreferencesForm,
    DetailsForm,
    NavigateAwayModal,
    SavedModal,
    Toast,
    Localization,
    NotFound,
    AlModal,
  },
  mixins: [Me, Translator],
  async created() {
    const id = this.$route.params.id;
    const configs = await this.getRosterConfigurations();

    this.rosterConfigurationList = configs;
    this.isRosterConfigurationListLoaded = true;

    if (id) {
      try {
        this.currentStep = Number.MAX_SAFE_INTEGER;
        const record = await CapacityPlannerConfiguration.getConfiguration(id);
        this.record = record;
        this.configuration.from = this.convertUtcToLocalTime(
          record.startDate,
          record.timezone
        );
        this.configuration.to = this.convertUtcToLocalTime(
          record.endDate,
          record.timezone
        );
        this.configuration.timezone = record.timezone;
        this.configuration.forecast = { id: record.forecastId };
        this.configuration.rosterConfigurations = record.rosterConfigurationId;
        this.configuration.scenarios = record.scenarios;

        this.preferences.plannedLeaveTarget =
          record.defaultPlannedLeaveTargetPercentage;
        this.preferences.unplannedLeaveTarget =
          record.defaultUnplannedLeaveTargetPecentage;
        this.preferences.shrinkageTarget =
          record.defaultShrinkageTargetPercentage;
        this.preferences.attritionTarget = record.attritionTargetPercentage;
        this.preferences.nestTime = record.nestTime;
        this.preferences.unitOfWork = record.unitOfWork;
        this.preferences.paidDaily = record.paidHoursPerDay;
        this.preferences.paidMonthly = record.paidHoursPerMonth;
        this.details.name = record.name;
        this.details.description = record.description;
        this.details.permission = record.businessRoles;
      } catch (err) {
        this.showNotFoundPage = true;
      }
    }

    this.isLoading = false;
    this.isOpen = true;
    this.oldCapacityPlan = this.storeCapacityPlanData();
  },
  data() {
    return {
      modelObj: undefined,
      isRoleMatchModel: false,
      oldCapacityPlan: {},
      headerText: "",
      bodyText: "",
      showNotFoundPage: false,
      isLoading: this.$route.params.id ? true : false,
      error: "",
      currentStep: 0,
      canSubmit: false,
      isSubmitted: false,
      record: null,
      leavePage: null,
      showDeletedToast: false,
      showCreatedToast: false,
      showSavedModal: false,
      showFailedToast: false,
      rosterConfigurationList: [],
      configuration: {
        from: "",
        to: "",
        rosterConfigurations: [],
        timezone: "",
        forecast: "",
        scenarios: [],
      },
      preferences: {
        plannedLeaveTarget: 0,
        unplannedLeaveTarget: 0,
        shrinkageTarget: 0,
        attritionTarget: 0,
        nestTime: 0,
        unitOfWork: "FTE",
        paidDaily: 0,
        paidMonthly: 0,
      },
      details: {
        name: "",
        description: "",
        permission: [],
      },
      headCounts: [],
      isRosterConfigurationListLoaded: false,
    };
  },
  computed: {
    roleMatchModalConfig() {
      return {
        header: this.translate("role-match-warning-header"),
        size: "sm",
        buttons: [
          {
            class: "is-secondary is-small",
            label: this.translate("role-match-warning-lable"),
            testId: "accept",
            command: async () => {
              await this.continueRoleMatchButton();
            },
          },
        ],
      };
    },
    onLeaveFunction() {
      return this.leavePage || (() => {});
    },
    isNewRecord() {
      return this.record?._id === undefined;
    },
    hasError() {
      return this.error !== "";
    },
    backToRoute() {
      return this.$route.name === "edit-configuration"
        ? "details-view"
        : "home";
    },
    isDirty() {
      if (!this.record) {
        return true;
      }
      const record = this.getAsModel();
      const diff = record.getDifference(this.record);

      return Object.keys(diff).length > 0;
    },
    configurationModel() {
      return {
        from: { required },
        to: { required, isAfter: DateValidators.isAfter("from") },
        rosterConfigurations: { required },
        timezone: { required },
        forecast: { required },
        scenarios: {},
      };
    },
    preferencesModel() {
      let conditionals = {};
      if (this.preferences.unitOfWork === "FTE") {
        conditionals = {
          paidDaily: {
            required,
            validateNegativeNumber: this.validateNegativeNumber,
            between: between(1, 99),
          },
          paidMonthly: {
            required,
            between: between(1, 999),
            paidMonthValidation: this.paidMonthValidation,
            validateNegativeNumber: this.validateNegativeNumber,
          },
        };
      }
      return {
        ...conditionals,
        plannedLeaveTarget: {
          required,
          validateNegativeNumber: this.validateNegativeNumber,
          between: between(0, 100),
        },
        unplannedLeaveTarget: {
          required,
          validateNegativeNumber: this.validateNegativeNumber,
          between: between(0, 100),
        },
        shrinkageTarget: {
          required,
          validateNegativeNumber: this.validateNegativeNumber,
          between: between(0, 100),
        },
        attritionTarget: {
          required,
          validateNegativeNumber: this.validateNegativeNumber,
          between: between(0, 100),
        },
        unitOfWork: { required },
        nestTime: {
          required,
          validateNegativeNumber: this.validateNegativeNumber,
          between: between(0, 100),
        },
      };
    },
    detailsModel() {
      return {
        name: { required, maxLength: maxLength(25) },
        description: { maxLength: maxLength(50) },
        permission: {},
      };
    },
    nextButtonText() {
      return this.translate("create-plan-next-button");
    },
    submitButtonText() {
      if (this.$route.params.id) {
        return this.translate("edit-plan-submit-button");
      }
      return this.translate("create-plan-submit-button");
    },
  },
  validations() {
    return {
      configuration: this.configurationModel,
      preferences: this.preferencesModel,
      details: this.detailsModel,
    };
  },
  methods: {
    async getRosterConfigurations() {
      return await RosterConfiguration.getRosterConfigurations({
        fields: "_id,name,workPlans,teams",
      });
    },
    async continueRoleMatchButton() {
      this.isRoleMatchModel = false;
      if (this.$route.params.id) {
        await this.updateCapacityPlan();
      } else {
        await this.insertCapacityPlan();
      }
    },
    closeIsRoleMatchWarningModal() {
      this.isRoleMatchModel = false;
    },
    convertUtcToLocalTime(date, timezone) {
      return utcToZonedTime(date, timezone);
    },
    validateNegativeNumber(field) {
      if (Math.sign(field) < 0) {
        return false;
      } else {
        return true;
      }
    },
    paidMonthValidation() {
      return Number(this.preferences.paidDaily) > 0 &&
        Number(this.preferences.paidMonthly) > 0
        ? Number(this.preferences.paidDaily) <
            Number(this.preferences.paidMonthly)
        : true;
    },
    async getUsersByTeamIds(teamIds) {
      return await User.getUsers({
        active: "all",
        teamIds: Array.from(teamIds),
        fields: "firstName=true,lastName=true,startAt=true,endAt=true",
      });
    },
    next(configuration) {
      if (this.$route.params.id) {
        this.configuration.rosterConfigurations =
          configuration.rosterConfigurations;
      }

      this.currentStep++;

      const currentElement = document.getElementById(
        `vertical-wizard-item-${this.currentStep}`
      );

      if (currentElement) {
        setTimeout(() => {
          currentElement.scrollIntoView({ behavior: "smooth" });
        });
      }
    },
    onInput() {
      this.$v.$touch();
      this.canSubmit = !this.isSubmitted && this.$v.$dirty && !this.$v.$error;
    },
    getAsModel() {
      const permission = this.details.permission ? this.details.permission : [];

      return new CapacityPlannerConfiguration({
        ...this.record,
        startDate: this.configuration.from,
        endDate: this.configuration.to,
        headCounts: this.headCounts,
        rosterConfigurationId: this.configuration.rosterConfigurations.map(
          (rc) => ({ _id: rc.id })
        ),
        timezone: this.configuration.timezone,
        forecastId: this.configuration.forecast.id,
        defaultPlannedLeaveTargetPercentage:
          this.preferences.plannedLeaveTarget,
        defaultUnplannedLeaveTargetPecentage:
          this.preferences.unplannedLeaveTarget,
        defaultShrinkageTargetPercentage: this.preferences.shrinkageTarget,
        attritionTargetPercentage: this.preferences.attritionTarget,
        nestTime: this.preferences.nestTime,
        unitOfWork: this.preferences.unitOfWork,
        paidHoursPerDay: this.preferences.paidDaily,
        paidHoursPerMonth: this.preferences.paidMonthly,
        name: this.details.name || "",
        description: this.details.description,
        businessRoles: permission.map((p) => ({ _id: p._id })),
        scenarios: this.configuration.scenarios.map((sc) => ({ _id: sc._id })),
      });
    },
    // map capacity plan records
    mapNewValueToObject(data) {
      const intialObject = {
        attritionTargetPercentage: 0,
        businessRoles: [],
        defaultPlannedLeaveTargetPercentage: 0,
        defaultShrinkageTargetPercentage: 0,
        defaultUnplannedLeaveTargetPecentage: 0,
        nestTime: 0,
        description: "",
        forecastId: 0,
        name: "",
        paidHoursPerDay: 0,
        paidHoursPerMonth: 0,
        rosterConfigurationId: [],
        timezone: "",
        unitOfWork: "",
        scenarios: [],
      };

      let finalObject = {};
      Object.keys(intialObject).filter((key) => {
        if (key in data) {
          finalObject[key] = data[key];
        }
      });
      return finalObject;
    },
    // this holds the old capacity plan data
    storeCapacityPlanData() {
      const permission = this.details.permission ? this.details.permission : [];
      return {
        startDate: this.configuration.from,
        endDate: this.configuration.to,
        rosterConfigurationId: this.configuration.rosterConfigurations.map(
          (rc) => ({ _id: rc._id })
        ),
        timezone: this.configuration.timezone,
        forecastId: this.configuration.forecast.id,
        defaultPlannedLeaveTargetPercentage:
          this.preferences.plannedLeaveTarget,
        defaultUnplannedLeaveTargetPecentage:
          this.preferences.unplannedLeaveTarget,
        defaultShrinkageTargetPercentage: this.preferences.shrinkageTarget,
        attritionTargetPercentage: this.preferences.attritionTarget,
        nestTime: this.preferences.nestTime,
        unitOfWork: this.preferences.unitOfWork,
        paidHoursPerDay: this.preferences.paidDaily,
        paidHoursPerMonth: this.preferences.paidMonthly,
        name: this.details.name || "",
        description: this.details.description,
        businessRoles: permission,
        scenarios: this.configuration.scenarios.map((sc) => ({ _id: sc._id })),
      };
    },
    async submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      if (this.configuration) {
        const { timezone, from, to } = this.configuration;
        const tzDates = this.formatDatesInTz({ from, to, timezone });

        this.configuration.from = tzDates.from;
        this.configuration.to = tzDates.to;
      }

      this.modelObj = this.getAsModel();

      const oldCapacityPlanData = this.mapNewValueToObject(
        this.oldCapacityPlan
      );
      const newCapacityPlanData = this.mapNewValueToObject(this.modelObj);

      // checked capacityPlan isEqual or not
      const isEqualCapacityPlan = equals(
        newCapacityPlanData,
        oldCapacityPlanData
      );

      try {
        if (this.$route.params.id) {
          if (isEqualCapacityPlan) {
            this.$router.push({
              name: "details-view",
              params: { id: this.modelObj._id },
            });
          } else {
            // update capacity plan
            const isRoleMatch = this.checkRoleMatchWarning(
              newCapacityPlanData.businessRoles
            );
            if (!isRoleMatch) {
              this.isRoleMatchModel = true;
            } else {
              await this.updateCapacityPlan();
            }
          }
        } else {
          // create new capacity plan
          const isRoleMatch = this.checkRoleMatchWarning(
            newCapacityPlanData.businessRoles
          );
          if (!isRoleMatch) {
            this.isRoleMatchModel = true;
          } else {
            await this.insertCapacityPlan();
          }
        }
      } catch (e) {
        this.error = "Failed to create capacity plan";
        this.isSubmitted = false;
        this.canSubmit = true;
        this.showFailedToast = true;
      }
    },
    async updateCapacityPlan() {
      this.isSubmitted = true;
      this.canSubmit = false;
      await this.modelObj.update();
      this.showSavedModal = true;
      this.model = this.modelObj;
    },
    async insertCapacityPlan() {
      this.isSubmitted = true;
      this.canSubmit = false;
      const newModel = await this.modelObj.insert();
      this.model = new CapacityPlannerConfiguration(newModel);
      this.showCreatedToast = true;
      setTimeout(() => {
        this.navigateToDetails();
      }, 2000);
    },
    checkRoleMatchWarning(businessRoles) {
      const userRoles = this.me.roles || [];
      const isRoleMatch = this.checkRoleMatch(userRoles, businessRoles);
      return isRoleMatch;
    },
    checkRoleMatch(userRoles, businessRoles) {
      if (userRoles.length === 0 && businessRoles.length === 0) {
        return true;
      }
      return userRoles.some((role) => {
        if (businessRoles.length < 1) return true;

        return businessRoles?.some((businessRole) => {
          return businessRole._id === role._id;
        });
      });
    },
    navigateToDetails(model = this.model) {
      this.$router.push({
        name: "details-view",
        params: { id: model._id },
      });
    },
    navigateToListPage() {
      this.$router.push({
        name: "home",
      });
    },
    formatDatesInTz({ from, to, timezone }) {
      return {
        from: zonedTimeToUtc(from, timezone),
        to: zonedTimeToUtc(to, timezone),
      };
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !this.isSubmitted) {
      // check on edit ,capacity plan
      if (this.$route.params.id) {
        const model = this.getAsModel();

        const oldCapacityPlanData = this.mapNewValueToObject(
          this.oldCapacityPlan
        );
        const newCapacityPlanData = this.mapNewValueToObject(model);

        // checked capacityPlan isEqual or not
        let isEqualCapacityPlan = equals(
          newCapacityPlanData,
          oldCapacityPlanData
        );
        if (!isEqualCapacityPlan) {
          this.showNavigateAwayModal = true;
          this.leavePage = next;
        } else {
          next();
        }
      }
    } else {
      next();
    }
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss"></style>
